import {useCallback, useRef, useState} from 'react';
import {
    fetchFullVehicleDetails,
    selectFullVehicleDetails,
    selectVehicleDetailsError,
    selectVehicleDetailsLoading,
    setOpenedVehicleCardVin,
} from '../../vehicleDetailsSlice';
import {useAppDispatch, useAppSelector} from '../../../app/hooks';
import {useLinkHandler} from '../../../app/hooks/useLinkHandler';
import {botIdSelector, websiteLocationSelector} from '../../chatbotSlice';
import {VehicleDataDTO} from "../../../api/generated";

export const useVehicleDetails = () => {
    const dispatch = useAppDispatch();
    const publicBotId = useAppSelector(botIdSelector);
    const websiteLocation = useAppSelector(websiteLocationSelector);
    const {openLink} = useLinkHandler();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleDataDTO | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const [attemptedFetches, setAttemptedFetches] = useState<Record<string, boolean>>({});

    const fullVehicleDetails = useAppSelector((state) =>
        selectedVehicle ? selectFullVehicleDetails(state, selectedVehicle.vin) : undefined
    );
    const isLoading = useAppSelector((state) =>
        selectedVehicle ? selectVehicleDetailsLoading(state, selectedVehicle.vin) : false
    );
    const error = useAppSelector((state) =>
        selectedVehicle ? selectVehicleDetailsError(state, selectedVehicle.vin) : null
    );

    const setModalRef = useCallback((ref: HTMLDivElement | null) => {
        modalRef.current = ref;
    }, []);

    const handleFetchVehicleDetails = (vehicle: VehicleDataDTO) => {
        if (vehicle.vin &&
            publicBotId &&
            !fullVehicleDetails &&
            !isLoading &&
            !attemptedFetches[vehicle.vin]) {

            setAttemptedFetches(prev => ({...prev, [vehicle.vin]: true}));
            dispatch(fetchFullVehicleDetails({vin: vehicle.vin, publicBotId}));
        }
    }

    const openVehicleDetails = useCallback((vehicle: VehicleDataDTO) => {
        handleFetchVehicleDetails(vehicle);

        setSelectedVehicle(vehicle);
        dispatch(setOpenedVehicleCardVin(vehicle.vin));
        setIsOpen(true);
    }, [dispatch, fullVehicleDetails, isLoading, publicBotId, attemptedFetches]);

    const handleOpenVehicleDetailsClick = useCallback((vehicle: VehicleDataDTO) => {
        if (isOpen && selectedVehicle && selectedVehicle.vin === vehicle.vin) {
            return;
        }

        if (vehicle.redirect_url) {
            dispatch(setOpenedVehicleCardVin(vehicle.vin));
            const redirectTimer = setTimeout(() => {
                openLink(vehicle.redirect_url, false);
            }, 1000);

            return () => clearTimeout(redirectTimer);
        }

        handleFetchVehicleDetails(vehicle);

        setSelectedVehicle(vehicle);
        dispatch(setOpenedVehicleCardVin(vehicle.vin));
        setIsOpen(true);
    }, [dispatch, isOpen, selectedVehicle, publicBotId, fullVehicleDetails, isLoading, websiteLocation, attemptedFetches]);

    const closeVehicleDetails = useCallback(() => {
        if (modalRef.current) {
            modalRef.current.style.transform = 'translateY(100%)';

            setTimeout(() => {
                setIsOpen(false);
                setSelectedVehicle(null);
                dispatch(setOpenedVehicleCardVin(null));
            }, 300);
        } else {
            setIsOpen(false);
            dispatch(setOpenedVehicleCardVin(null));
        }
    }, [dispatch, modalRef]);

    return {
        isOpen,
        selectedVehicle,
        openVehicleDetails,
        handleOpenVehicleDetailsClick,
        closeVehicleDetails,
        setModalRef,
        isLoading,
        error,
        fullVehicleDetails
    };
};
